import { useCallback, useEffect, useRef, useState } from 'react';
import { setSettingsDirty } from './settingsDirty';

/**
 * Fingerprint used for the dirty check: empty string, null and undefined are all
 * the SAME "no value". Without this, typing a rejected char (sanitized to '')
 * into a field that was null, or clearing a field back to blank, would falsely
 * register as an edit.
 */
export const dirtyFingerprint = (obj: unknown): string =>
    JSON.stringify(obj, (_k, v) => {
        if (v === '' || v === undefined) return null;
        // A backend carrega números (15) mas os campos NumberWithSuffix guardam o
        // valor editado como string ("15") — trate "15" e 15 como o MESMO valor,
        // senão editar-e-voltar num campo numérico ficaria eternamente "sujo".
        // "25620-000" e outros não-numéricos não são afetados (Number → NaN).
        if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
            return Number(v);
        }
        return v;
    });

/**
 * DERIVED dirty state (QA-STANDARDS §5.10): `isModified` comes from comparing the
 * live form to a baseline snapshot — NOT a boolean flipped on every onChange.
 * This is the single fix for two bugs the Settings tabs kept hitting:
 *  - typing a rejected character shows "Salvar" though nothing changed;
 *  - editing a field and returning it to its original value keeps it dirty.
 *
 * Usage:
 *   const { isModified, setBaseline } = useDirtyTracker(formData);
 *   // after loading OR after a successful save:
 *   setBaseline(loadedOrSavedData);
 *
 * Mirrors into `settingsDirty` so the Settings tab shells warn before leaving.
 */
export function useDirtyTracker<T>(current: T | null | undefined): {
    isModified: boolean;
    setBaseline: (val: T | null | undefined) => void;
} {
    const baselineRef = useRef<string>('');
    const [isModified, setIsModified] = useState(false);

    useEffect(() => {
        const dirty =
            current != null &&
            baselineRef.current !== '' &&
            dirtyFingerprint(current) !== baselineRef.current;
        setIsModified(dirty);
        setSettingsDirty(dirty);
        return () => setSettingsDirty(false);
    }, [current]);

    const setBaseline = useCallback((val: T | null | undefined) => {
        baselineRef.current = val == null ? '' : dirtyFingerprint(val);
        // Setting a baseline defines the new CLEAN state — clear the dirty flag
        // now. Otherwise, calling setBaseline after a save WITHOUT also changing
        // `current` (same reference) leaves the effect un-triggered and the
        // "unsaved changes" bar stuck on (e.g. the Notifications tab: pick a
        // template → Salvar → bar never went away).
        setIsModified(false);
        setSettingsDirty(false);
    }, []);

    return { isModified, setBaseline };
}
